// ModuleStepContent — the ONE read-only renderer for a single course step's
// content, keyed by step type (info/task body markdown, checkpoint & quiz
// options with the correct answer marked, confirmation). Extracted from
// CourseStepsPreview's StepCard so the module detail page (expandable steps),
// the canvas "Förhandsgranska", and Link's artifact preview render step content
// identically. Content field names match both the backend player and the
// canonical create/edit shape.
import React from 'react';
import { Check } from 'lucide-react';
import { MarkdownContent } from './MarkdownContent';

// Does this step have anything worth expanding?
export const hasStepContent = (stepType: string, content: any): boolean => {
    const c = content ?? {};
    switch (stepType) {
        case 'info':
        case 'task':
            return !!(c.body || c.image_url || c.video_url || (Array.isArray(c.sub_points) && c.sub_points.length));
        case 'checkpoint':
            return !!(c.question_text || (Array.isArray(c.options) && c.options.length));
        case 'quiz':
            return Array.isArray(c.questions) && c.questions.length > 0;
        case 'confirmation':
            return !!(c.body || c.checkbox_label);
        default:
            return !!c && Object.keys(c).length > 0;
    }
};

// One option row (checkpoint / quiz) with the correct answer highlighted.
const OptionRow: React.FC<{ opt: any; correctId?: string }> = ({ opt, correctId }) => {
    const isCorrect = opt?.option_id && correctId && opt.option_id === correctId;
    return (
        <li className={`flex items-start gap-2 px-2.5 py-1.5 rounded-lg text-sm ${isCorrect ? 'bg-green-50 border border-green-200 text-green-900' : 'text-gray-700'}`}>
            <span className="font-mono text-xs mt-0.5 text-gray-400">{opt?.option_id}</span>
            <span className="flex-1">{opt?.text}</span>
            {isCorrect && <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />}
        </li>
    );
};

interface ModuleStepContentProps {
    stepType: string;
    content: any;
}

export const ModuleStepContent: React.FC<ModuleStepContentProps> = ({ stepType, content }) => {
    const c = content ?? {};

    if (stepType === 'info' || stepType === 'task') {
        return (
            <div className="space-y-2">
                {c.image_url && <img src={c.image_url} alt="" className="max-h-56 rounded-lg border border-gray-100 object-cover" />}
                {c.video_url && <span className="inline-flex items-center gap-1 text-xs text-[#1A4D4F]">🎬 Video bifogad</span>}
                {c.body ? <MarkdownContent>{String(c.body)}</MarkdownContent> : <p className="text-sm text-gray-400 italic">Inget innehåll ännu.</p>}
                {Array.isArray(c.sub_points) && c.sub_points.length > 0 && (
                    <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                        {c.sub_points.map((p: string, i: number) => <li key={i}>{p}</li>)}
                    </ul>
                )}
            </div>
        );
    }

    if (stepType === 'checkpoint') {
        return (
            <div className="space-y-2">
                {c.question_text && <p className="text-sm text-gray-800">{c.question_text}</p>}
                {Array.isArray(c.options) && (
                    <ul className="space-y-1">
                        {c.options.map((o: any, i: number) => <OptionRow key={i} opt={o} correctId={c.correct_option_id} />)}
                    </ul>
                )}
                {c.explanation && <p className="text-xs text-gray-500 italic">{c.explanation}</p>}
            </div>
        );
    }

    if (stepType === 'quiz') {
        return (
            <div className="space-y-3">
                {/* "Godkäntgräns: 80%" is NOT shown, deliberately (#295): nothing
                    enforces it. The course form has no field for it and hardcodes
                    1.0, only Link ever wrote 0.8, and grading requires every answer
                    correct. Displaying a bar the product does not apply is a promise
                    it does not keep. Bring this back with #43, where partial pass
                    becomes an opt-in the employer can actually set. */}
                <p className="text-xs text-gray-500">Alla frågor måste besvaras rätt.</p>
                {Array.isArray(c.questions) && c.questions.map((q: any, qi: number) => (
                    <div key={qi} className="border-l-2 border-[#A4C3B2] pl-3">
                        <p className="text-sm font-medium text-gray-800">{qi + 1}. {q?.question_text}</p>
                        {Array.isArray(q?.options) && (
                            <ul className="space-y-1 mt-1">
                                {q.options.map((o: any, oi: number) => <OptionRow key={oi} opt={o} correctId={q.correct_option_id} />)}
                            </ul>
                        )}
                        {q?.explanation && <p className="text-xs text-gray-500 italic mt-1">{q.explanation}</p>}
                    </div>
                ))}
                {(!Array.isArray(c.questions) || c.questions.length === 0) && (
                    <p className="text-sm text-gray-400 italic">Inga frågor ännu.</p>
                )}
            </div>
        );
    }

    if (stepType === 'confirmation') {
        return (
            <div className="space-y-2">
                {c.body && <MarkdownContent>{String(c.body)}</MarkdownContent>}
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" disabled className="w-4 h-4 accent-[#1A4D4F]" />
                    {c.checkbox_label || 'Jag bekräftar'}
                </label>
            </div>
        );
    }

    // unknown — graceful fallback
    return <pre className="text-xs bg-gray-50 rounded-lg p-2 overflow-x-auto text-gray-600">{JSON.stringify(c, null, 2)}</pre>;
};

export default ModuleStepContent;
